import React, { Component } from "react";
import { Modal, Button, Container, Row, Col, Alert } from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import moment from "moment";
import SignatureCanvas from "react-signature-canvas";

export default class ViewContract extends Component {
  constructor(props) {
    super(props);

    this.state = {
      signatureData: null,
      showSignatureModal: false,
      manualSignatureDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
      processingManualSignature: false,
    };
    
    this.sigCanvas = React.createRef();
  }

  // Clear signature canvas
  clearSignature = () => {
    if (this.sigCanvas.current) {
      this.sigCanvas.current.clear();
    }
  };

  // Handle electronic signature process
  handleElectronicSign = () => {
    this.setState({ showSignatureModal: true });
  };

  // Complete electronic signature
  completeElectronicSignature = () => {
    if (this.sigCanvas.current && !this.sigCanvas.current.isEmpty()) {
      const signatureData = this.sigCanvas.current.toDataURL();
      this.setState({ 
        signatureData, 
        showSignatureModal: false 
      });
      
      // Call parent handler with signature data
      this.props.handleElectronicSignature(signatureData);
    } else {
      alert("Please provide a signature before continuing.");
    }
  };

  // Handle print contract
  handlePrintContract = () => {
    this.props.handlePrintContract();
  };

  // Handle manual signature completion
  handleManualSignatureComplete = () => {
    this.setState({ processingManualSignature: true });
    this.props.handleManualSignature(this.state.manualSignatureDate);
  };

  // Handle date change for manual signature
  handleDateChange = (event) => {
    this.setState({ manualSignatureDate: event.target.value });
  };

  render() {
    if (this.props.currentStep !== 2) {
      return null;
    }

    // Add safety checks for required data
    if (!this.props.customer || !this.props.request || !this.props.dealer || !this.props.installer) {
      return (
        <div className="text-center p-4">
          <p>Loading contract information...</p>
        </div>
      );
    }

    let dealerAddress = `${this.props.dealer.dealer_address_line1}, ${this.props.dealer.dealer_address_city}, ${this.props.dealer.dealer_address_state} ${this.props.dealer.dealer_address_zip}`;

    let contractData = {
      customerName: this.props.customer.customer_name,
      customerAddress: this.props.customer.customer_address,
      customerEmail: this.props.customer.customer_Email_Address,
      vehicleYear: this.props.request.vehicleYear,
      vehicleMake: this.props.request.vehicleMake,
      vehicleModel: this.props.request.vehicleModel,
      vehicleTrim: this.props.request.vehicleTrim,
      vin: this.props.vehicle?.vin,
      dealerName: this.props.dealer.dealer_name,
      dealerAddress: dealerAddress,
      installerName: this.props.installer.Installer_Name,
      installerEmail: this.props.installer.installer_email,
      date: moment().format("MM/DD/YYYY"),
    };

    const services = this.props.services || [];
    const columns = [
      {
        dataField: "category",
        text: "Category",
      },
      {
        dataField: "desc",
        text: "Service",
      },
      {
        dataField: "amount",
        text: "Amount",
      },
    ];

    let services_ = [];
    for (let i = 0; i < services.length; i++) {
      let rowView = {
        serviceId: "",
        category: "",
        desc: "",
        amount: "",
      };
      rowView.serviceId = services[i].service_id;
      rowView.category = services[i].service_category;
      rowView.desc = services[i].description;
      
      // UPDATED: Use dealer's retail price instead of service_cost
      if (typeof services[i].service_amount === "string") {
        // Use retail_price if available (dealer's override), otherwise fall back to service_cost
        const cost = parseFloat(services[i].retail_price || services[i].service_cost);
        rowView.amount = isNaN(cost) ? "$0.00" : "$" + cost.toFixed(2);
      } else {
        // Use retail_price if available (dealer's override), otherwise fall back to service_cost
        const cost = parseFloat(services[i].retail_price || services[i].service_cost);
        rowView.amount = isNaN(cost) ? "$0.00" : "$" + cost.toFixed(2);
      }
      services_.push(rowView);
    }

    return (
      <div className="d-flex flex-column align-items-center justify-content-center">
        
        {/* Contract completion status */}
        {this.props.contractCompleted && (
          <Alert variant="success" className="w-100 text-center">
            <Alert.Heading>Contract Process Complete!</Alert.Heading>
            
            {/* Electronic Signature Completed */}
            {this.props.contractAction === 'electronic' && (
              <p>Contract has been signed electronically and copies have been emailed to all parties.</p>
            )}
            
            {/* Manual Signature - Downloaded but not yet completed */}
            {this.props.contractAction === 'print' && !this.props.manualSignatureCompleted && (
              <div>
                <p className="mb-3">Contract has been downloaded. Complete the manual signing process below:</p>
                <div className="d-flex flex-column align-items-center">
                  <div className="mb-3">
                    <label htmlFor="signatureDate" className="form-label me-2">
                      <strong>Date Signed:</strong>
                    </label>
                    <input
                      type="date"
                      id="signatureDate"
                      className="form-control d-inline-block"
                      style={{width: 'auto'}}
                      value={this.state.manualSignatureDate}
                      onChange={this.handleDateChange}
                      disabled={this.state.processingManualSignature}
                    />
                  </div>
                  <Button 
                    variant="primary" 
                    onClick={this.handleManualSignatureComplete}
                    disabled={this.state.processingManualSignature}
                  >
                    {this.state.processingManualSignature && (
                      <span className="me-2">
                        <i className="fa-solid fa-arrows-rotate fa-spin" />
                      </span>
                    )}
                    Complete Manual Signature
                  </Button>
                </div>
              </div>
            )}
            
            {/* Manual Signature - Completed */}
            {this.props.contractAction === 'print' && this.props.manualSignatureCompleted && (
              <p>Manual signature completed. Copies have been emailed to all parties.</p>
            )}
          </Alert>
        )}

        {/* Contract Summary and Options */}
        {!this.props.contractCompleted && (
          <div className="w-100">
            {/* Contract Summary */}
            <div className="bg-light border rounded p-4 mb-4">
              <h4 className="text-center mb-4">Contract Summary</h4>
              
              <Row className="mb-3">
                <Col md={6}>
                  <div className="bg-white p-3 rounded border">
                    <h6 className="text-primary mb-2">Customer Information</h6>
                    <p className="mb-1"><strong>Name:</strong> {contractData.customerName}</p>
                    <p className="mb-1"><strong>Address:</strong> {contractData.customerAddress}</p>
                    <p className="mb-1"><strong>Email:</strong> {contractData.customerEmail}</p>
                    <p className="mb-0"><strong>Date:</strong> {contractData.date}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-white p-3 rounded border">
                    <h6 className="text-primary mb-2">Vehicle Information</h6>
                    <p className="mb-1">
                      <strong>{contractData.vehicleYear} {contractData.vehicleMake}</strong>
                    </p>
                    <p className="mb-1">
                      {contractData.vehicleModel} {contractData.vehicleTrim}
                    </p>
                    {contractData.vin && !contractData.vin.startsWith('NV') && (
                      <p className="mb-0"><strong>VIN:</strong> {contractData.vin}</p>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <div className="bg-white p-3 rounded border">
                    <h6 className="text-primary mb-2">Installer</h6>
                    <p className="mb-0">{contractData.installerName}</p>
                  </div>
                </Col>
              </Row>

              {/* Services Summary */}
              <div className="bg-white p-3 rounded border mb-3">
                <h6 className="text-primary mb-2">Selected Services</h6>
                {services_.length > 0 ? (
                  <div className="row">
                    {services_.map((service, index) => (
                      <div key={index} className="col-md-6 mb-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-truncate me-2" title={service.desc}>
                            <small className="text-muted">{service.category}:</small><br/>
                            {service.desc}
                          </span>
                          <strong className="text-success">{service.amount}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-0">No services selected</p>
                )}
              </div>

              {/* Total Calculation */}
              {services_.length > 0 && (
                <div className="bg-light border p-3 rounded text-center">
                  <strong>
                    Total Services: {services_.length} | 
                    Total Amount: {services_.reduce((total, service) => {
                      const amount = parseFloat(service.amount.replace('$', '').replace(',', ''));
                      return total + (isNaN(amount) ? 0 : amount);
                    }, 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  </strong>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="d-flex flex-column align-items-center text-center">
              <div className="d-flex gap-3 mt-3">
                <Button 
                  variant="success" 
                  size="lg"
                  onClick={this.handleElectronicSign}
                  disabled={this.props.loading}
                >
                  {this.props.loading && this.props.contractAction === 'electronic' && (
                    <span className="me-2">
                      <i className="fa-solid fa-arrows-rotate fa-spin" />
                    </span>
                  )}
                  Sign Electronically
                </Button>
                
                <Button 
                  variant="info" 
                  size="lg"
                  onClick={this.handlePrintContract}
                  disabled={this.props.loading}
                >
                  {this.props.loading && this.props.contractAction === 'print' && (
                    <span className="me-2">
                      <i className="fa-solid fa-arrows-rotate fa-spin" />
                    </span>
                  )}
                  Download Contract PDF
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Contract Display Modal */}
        <Modal
          show={this.props.show}
          onHide={this.props.handleClose}
          size="xl"
          centered
        >
          <div id="contract-modal">
            <Modal.Header closeButton>
              <Modal.Title>
                Paint Protection Film & Window Tint Purchase Agreement
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Container>
                <Row className="mb-3">
                  <Col>
                    <h6>
                      This agreement is made and entered into on{" "}
                      <strong>{contractData.date}</strong>
                    </h6>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <h5>CUSTOMER INFORMATION</h5>
                    <p>
                      <strong>Name:</strong> {contractData.customerName}
                    </p>
                    <p>
                      <strong>Address:</strong> {contractData.customerAddress}
                    </p>
                    <p>
                      <strong>Email:</strong> {contractData.customerEmail}
                    </p>
                  </Col>
                </Row>
                <p></p>
                <Row className="mb-3">
                  <Col>
                    <h5>VEHICLE INFORMATION</h5>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <strong>Year:</strong> {contractData.vehicleYear}
                  </Col>
                  <Col>
                    <strong>Make:</strong> {contractData.vehicleMake}
                  </Col>
                  <Col>
                    <strong>Model:</strong> {contractData.vehicleModel}
                  </Col>
                  <Col>
                    <strong>Trim:</strong> {contractData.vehicleTrim}
                  </Col>
                </Row>
                <p></p>
                <Row className="mb-3">
                  <Col>
                    <h5>DEALER INFORMATION</h5>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <strong>Name:</strong> {contractData.dealerName}
                  </Col>
                  <Col>
                    <strong>Address:</strong> {contractData.dealerAddress}
                  </Col>
                </Row>
                <p></p>
                <Row className="mb-3">
                  <Col>
                    <h5>CERTIFIED INSTALLER INFORMATION</h5>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <strong>Name:</strong> {contractData.installerName}
                  </Col>
                  <Col>
                    <strong>Email:</strong> {contractData.installerEmail}
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <h5>PURCHASED PACKAGES</h5>
                    <BootstrapTable
                      keyField="serviceId"
                      data={services_}
                      columns={columns}
                    />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <h5>TERMS AND CONDITIONS</h5>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <p>
                      <strong>Professional Installation:</strong> The
                      dealership utilizes {contractData.installerName} as a
                      professional installer to complete the work. The
                      performance of the installation will adhere to industry
                      standards.
                    </p>
                    <p>
                      <strong>Issue Resolution:</strong> Any concerns or issues
                      regarding the work performed should be addressed
                      in-writing by contacting the installer directly via email
                      at: {contractData.installerEmail}. Customers are
                      encouraged to schedule a consultation for any
                      installation-related inquiries.
                    </p>
                    <p>
                      <strong>Limitation of Liability:</strong> The dealership
                      shall not be held liable for any issues arising from the
                      installation of the package.
                    </p>
                  </Col>
                </Row>
              </Container>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={this.props.handleClose}>
                Close
              </Button>
            </Modal.Footer>
          </div>
        </Modal>

        {/* Electronic Signature Modal */}
        <Modal
          show={this.state.showSignatureModal}
          onHide={() => this.setState({ showSignatureModal: false })}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Electronic Signature</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center mb-3">
              <p>Please have the customer sign in the box below:</p>
            </div>
            <div className="d-flex justify-content-center">
              <SignatureCanvas
                ref={this.sigCanvas}
                penColor="black"
                backgroundColor="rgba(240, 240, 240, 1)"
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: "border rounded",
                }}
              />
            </div>
            <div className="text-center mt-3">
              <Button variant="outline-secondary" onClick={this.clearSignature}>
                Clear Signature
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => this.setState({ showSignatureModal: false })}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={this.completeElectronicSignature}
              disabled={this.props.loading}
            >
              {this.props.loading && (
                <span className="me-2">
                  <i className="fa-solid fa-arrows-rotate fa-spin" />
                </span>
              )}
              Complete Signature
            </Button>
          </Modal.Footer>
        </Modal>

      </div>
    );
  }
}